import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FlaskConical, ShieldCheck, FileBadge, Download } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { SPEC_PRODUCTS, SPEC_VERSION } from "../data/specs";
import { downloadCoaPdf, downloadMsdsPdf } from "../utils/specPdf";

const CATS = [
  { id: "flavours", label: "Food Flavours" },
  { id: "caramel", label: "Caramel Colours" },
];

export default function Quality() {
  const [params] = useSearchParams();
  const initial = SPEC_PRODUCTS.find((p) => p.name === params.get("product")) || SPEC_PRODUCTS[0];
  const [selected, setSelected] = useState(initial);
  const [tab, setTab] = useState("coa");

  const msdsSections = [
    { no: "01", title: "Hazard Identification", body: selected.msds.hazard },
    { no: "02", title: "Composition / Ingredients", body: selected.msds.composition },
    { no: "03", title: "First-Aid Measures", list: selected.msds.firstAid.map((f) => `${f.route}: ${f.action}`) },
    { no: "04", title: "Handling & Storage", body: selected.msds.handling },
    { no: "05", title: "Physical & Chemical Properties", pairs: selected.msds.properties },
    { no: "06", title: "Stability & Reactivity", body: selected.msds.stability },
    { no: "07", title: "Disposal Considerations", body: selected.msds.disposal },
    { no: "08", title: "Transport Information", body: selected.msds.transport },
  ];

  return (
    <main data-testid="quality-page" className="pt-[72px]">
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pt-20 sm:pt-32 pb-16">
        <p className="overline-label mb-8">Product-Wise Documentation</p>
        <h1 className="font-display font-black tracking-tighter leading-[0.9] text-[clamp(3rem,9vw,8rem)]" data-testid="quality-heading">
          <MaskedLine delay={0.1}>COA &amp; MSDS,</MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="text-[#D97736]">PER PRODUCT.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-12 max-w-2xl text-base sm:text-lg leading-relaxed text-[#f5f5f5]/70">
            Nineteen documented products, each with its own Certificate of Analysis
            and Safety Data Sheet. Select a product to view its full specification
            and safety profile.
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pb-24 sm:pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Product selector */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 border border-white/10" data-testid="spec-product-list">
              {CATS.map((cat) => (
                <div key={cat.id}>
                  <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-[#D97736] px-6 pt-6 pb-3 border-b border-white/10">
                    {cat.label}
                  </p>
                  {SPEC_PRODUCTS.filter((p) => p.category === cat.id).map((p) => (
                    <button
                      key={p.sdsNo}
                      data-testid={`spec-select-${p.sdsNo}`}
                      onClick={() => setSelected(p)}
                      className={`w-full text-left flex items-center justify-between gap-4 px-6 py-4 border-b border-white/10 transition-colors duration-300 ${
                        selected.sdsNo === p.sdsNo
                          ? "bg-[#D97736]/10 text-[#D97736]"
                          : "text-[#f5f5f5]/75 hover:bg-[#111111] hover:text-[#f5f5f5]"
                      }`}
                    >
                      <span className="font-display text-base font-bold tracking-tight">{p.name}</span>
                      <span className="font-mono-jb text-[10px] text-[#f5f5f5]/40 shrink-0">{p.sdsNo}</span>
                    </button>
                  ))}
                </div>
              ))}
              <p className="px-6 py-5 font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#f5f5f5]/40 leading-relaxed">
                Mint, Tomato &amp; industrial ingredient specs issued on request.
              </p>
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={selected.sdsNo}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="border border-white/10 p-8 sm:p-10 mb-6" data-testid="spec-header">
                  <div className="flex flex-wrap items-start justify-between gap-6">
                    <div>
                      <p className="font-mono-jb text-[10px] uppercase tracking-[0.3em] text-[#D97736] mb-3">
                        {selected.sdsNo} · Rev {SPEC_VERSION.version} · {SPEC_VERSION.issueDate}
                      </p>
                      <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tighter">{selected.name}</h2>
                      <p className="mt-3 text-sm text-[#f5f5f5]/60">{selected.description}</p>
                    </div>
                    <FileBadge className="w-8 h-8 text-[#D97736] shrink-0" strokeWidth={1.5} />
                  </div>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      data-testid="download-coa-pdf"
                      onClick={() => downloadCoaPdf(selected)}
                      className="group flex items-center gap-2.5 bg-[#D97736] text-[#0a0a0a] font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-6 py-3 rounded-full transition-colors duration-300 hover:bg-[#e8904f]"
                    >
                      <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                      COA PDF
                    </button>
                    <button
                      data-testid="download-msds-pdf"
                      onClick={() => downloadMsdsPdf(selected)}
                      className="group flex items-center gap-2.5 border border-white/25 text-[#f5f5f5] font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-6 py-3 rounded-full transition-colors duration-300 hover:border-[#D97736] hover:text-[#D97736]"
                    >
                      <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                      MSDS PDF
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 mb-8" data-testid="spec-tabs">
                  <button
                    data-testid="spec-tab-coa"
                    onClick={() => setTab("coa")}
                    className={`flex items-center gap-2 font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-6 py-3 rounded-full border transition-colors duration-300 ${
                      tab === "coa"
                        ? "bg-[#D97736] border-[#D97736] text-[#0a0a0a]"
                        : "border-white/20 text-[#f5f5f5]/70 hover:border-[#D97736] hover:text-[#D97736]"
                    }`}
                  >
                    <FlaskConical className="w-4 h-4" /> Certificate of Analysis
                  </button>
                  <button
                    data-testid="spec-tab-msds"
                    onClick={() => setTab("msds")}
                    className={`flex items-center gap-2 font-mono-jb text-xs uppercase tracking-[0.15em] font-bold px-6 py-3 rounded-full border transition-colors duration-300 ${
                      tab === "msds"
                        ? "bg-[#D97736] border-[#D97736] text-[#0a0a0a]"
                        : "border-white/20 text-[#f5f5f5]/70 hover:border-[#D97736] hover:text-[#D97736]"
                    }`}
                  >
                    <ShieldCheck className="w-4 h-4" /> Safety Data Sheet
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  {tab === "coa" ? (
                    <motion.div
                      key="coa"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      data-testid="coa-table"
                    >
                      <div className="border-t border-white/10">
                        {selected.coa.map((row, i) => (
                          <div
                            key={row.param}
                            className="grid grid-cols-[140px_1fr] sm:grid-cols-[220px_1fr_auto] gap-4 items-baseline px-2 sm:px-6 py-5 border-b border-white/10 transition-colors duration-300 hover:bg-[#111111]"
                            data-testid={`coa-row-${i}`}
                          >
                            <span className="font-mono-jb text-[11px] uppercase tracking-[0.15em] text-[#f5f5f5]/50">
                              {row.param}
                            </span>
                            <span className="text-sm sm:text-base text-[#f5f5f5]/90 leading-relaxed">{row.value}</span>
                            {row.ref && (
                              <span className="hidden sm:block font-mono-jb text-[10px] uppercase tracking-[0.15em] text-[#D97736]/80">
                                {row.ref}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {selected.category === "caramel" && (
                        <p className="mt-6 font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#f5f5f5]/40">
                          * Internal proposed QC limit; verify applicable regulation
                        </p>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="msds"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      data-testid="msds-sections"
                    >
                      <div className="border-t border-l border-white/10 grid grid-cols-1 sm:grid-cols-2">
                        {msdsSections.map((s) => (
                          <div
                            key={s.no}
                            className="border-r border-b border-white/10 p-7 sm:p-8 transition-colors duration-300 hover:bg-[#111111]"
                            data-testid={`msds-section-${s.no}`}
                          >
                            <p className="font-mono-jb text-[10px] tracking-[0.3em] text-[#D97736] mb-3">{s.no}</p>
                            <h3 className="font-display text-lg font-bold tracking-tight mb-3">{s.title}</h3>
                            {s.body && <p className="text-sm leading-relaxed text-[#f5f5f5]/60">{s.body}</p>}
                            {s.list && (
                              <ul className="space-y-2">
                                {s.list.map((item) => (
                                  <li key={item} className="text-sm leading-relaxed text-[#f5f5f5]/60 flex gap-2">
                                    <span className="w-3 h-px bg-[#D97736] mt-2.5 shrink-0" />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            )}
                            {s.pairs && (
                              <div className="space-y-2">
                                {s.pairs.map((p) => (
                                  <p key={p.param} className="text-sm text-[#f5f5f5]/60">
                                    <span className="text-[#f5f5f5]/85 font-medium">{p.param}:</span> {p.value}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>
    </main>
  );
}
